import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api, resolveImage } from "@/lib/api";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Reveal } from "@/hooks/useReveal";
import { ArrowLeft, ArrowRight } from "lucide-react";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const res = await api.get(`/posts/${slug}`);
        setData(res.data);
      } catch (err) {
        if (err?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <main className="bg-black text-white min-h-screen">
        <Navigation alwaysDark />
        <div className="pt-40 px-6 md:px-12 max-w-3xl mx-auto">
          <p className="text-sm text-white/40">Loading…</p>
        </div>
      </main>
    );
  }

  if (notFound || !data?.post) {
    return (
      <main className="bg-black text-white min-h-screen">
        <Navigation alwaysDark />
        <div className="pt-40 pb-40 px-6 md:px-12 max-w-3xl mx-auto text-center">
          <p className="overline mb-4 text-[#B8945A]">404</p>
          <h1 className="font-heading text-4xl md:text-5xl mb-8">Post not found</h1>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#B8945A] hover:text-white"
          >
            <ArrowLeft size={14} /> Back to all posts
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const { post, prev, next } = data;

  return (
    <main data-testid="blog-post-page" className="bg-black text-white min-h-screen">
      <Navigation alwaysDark />

      {/* Hero image */}
      <section className="pt-24 md:pt-28">
        {post.hero_image_url ? (
          <div className="relative w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden bg-[#0a0a0a]">
            <img
              src={resolveImage(post.hero_image_url)}
              alt={post.title}
              className="w-full h-full object-cover"
              data-testid="blog-post-hero"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/40 pointer-events-none" />
          </div>
        ) : (
          <div className="w-full aspect-[16/9] md:aspect-[21/9] bg-gradient-to-br from-neutral-900 to-black" />
        )}
      </section>

      {/* Title + meta */}
      <section className="px-6 md:px-12 pt-16 md:pt-20">
        <div className="max-w-[720px] mx-auto">
          <Reveal>
            <Link
              to="/blog"
              data-testid="blog-post-back"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#B8945A] hover:text-white transition-colors mb-10"
            >
              <ArrowLeft size={14} /> Back to all posts
            </Link>
          </Reveal>

          <Reveal delay={100}>
            <p className="text-[10px] tracking-[0.32em] uppercase mb-6 text-[#B8945A]">
              {post.category || "Article"}
            </p>
          </Reveal>

          <Reveal delay={150}>
            <h1
              data-testid="blog-post-title"
              className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.06]"
            >
              {post.title}
            </h1>
          </Reveal>

          <Reveal delay={200}>
            <p className="text-[11px] tracking-[0.28em] uppercase text-white/40 mt-8">
              {fmtDate(post.publish_date)}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Body */}
      <section className="px-6 md:px-12 pt-12 md:pt-16 pb-20">
        <div className="max-w-[680px] mx-auto">
          <Reveal delay={100}>
            <article
              data-testid="blog-post-body"
              className="prose-blog text-white/85 text-[17px] md:text-lg leading-[1.85]"
            >
              <ReactMarkdown
                components={{
                  p: ({ children }) => (
                    <p className="mb-7">{children}</p>
                  ),
                  strong: ({ children }) => (
                    <strong className="text-white font-semibold">{children}</strong>
                  ),
                  h2: ({ children }) => (
                    <h2 className="font-heading text-3xl md:text-4xl text-white mt-14 mb-6 tracking-tight">
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="font-heading text-2xl text-white mt-12 mb-4 tracking-tight">
                      {children}
                    </h3>
                  ),
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#B8945A] underline underline-offset-4 hover:text-white transition-colors"
                    >
                      {children}
                    </a>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc pl-6 mb-7 space-y-2">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal pl-6 mb-7 space-y-2">{children}</ol>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-2 border-[#B8945A] pl-6 my-8 italic text-white/70">
                      {children}
                    </blockquote>
                  ),
                }}
              >
                {post.body || ""}
              </ReactMarkdown>
            </article>
          </Reveal>
        </div>
      </section>

      {/* CTA block */}
      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-[720px] mx-auto border border-white/10 px-8 md:px-12 py-14 md:py-16 text-center">
          <p className="overline mb-5 text-[#B8945A]">Work with Coach Manzano</p>
          <h3 className="font-heading text-3xl md:text-4xl tracking-tight mb-6">
            Need a second set of eyes on your home?
          </h3>
          <p className="text-white/60 mb-10 max-w-md mx-auto leading-relaxed">
            Whether you&apos;re staying put or thinking about your next move, I&apos;m
            happy to walk through the choices that actually move the needle on
            value.
          </p>
          <Link
            to="/#contact"
            data-testid="blog-post-cta"
            className="inline-flex items-center justify-center px-10 py-4 bg-[#B8945A] text-black border border-[#B8945A] text-[11px] uppercase tracking-[0.28em] hover:bg-transparent hover:text-[#B8945A] transition-all duration-500"
          >
            Get in touch
          </Link>
        </div>
      </section>

      {/* Prev / Next */}
      <section className="px-6 md:px-12 pb-32 md:pb-40">
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 border-t border-white/10 pt-12">
          {prev ? (
            <Link
              to={`/blog/${prev.slug}`}
              data-testid="blog-post-prev"
              className="group block p-6 md:p-8 border border-white/10 hover:border-[#B8945A] transition-colors"
            >
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#B8945A] mb-4 inline-flex items-center gap-2">
                <ArrowLeft size={12} /> Previous
              </p>
              <p className="font-heading text-xl md:text-2xl leading-snug tracking-tight group-hover:text-[#B8945A] transition-colors">
                {prev.title}
              </p>
            </Link>
          ) : <div />}
          {next ? (
            <Link
              to={`/blog/${next.slug}`}
              data-testid="blog-post-next"
              className="group block p-6 md:p-8 border border-white/10 hover:border-[#B8945A] transition-colors text-right"
            >
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#B8945A] mb-4 inline-flex items-center gap-2 justify-end">
                Next <ArrowRight size={12} />
              </p>
              <p className="font-heading text-xl md:text-2xl leading-snug tracking-tight group-hover:text-[#B8945A] transition-colors">
                {next.title}
              </p>
            </Link>
          ) : <div />}
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default BlogPost;
